import React, { useState, useCallback } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// Copy of the main app's client/src/components/CreateStandardModal.js (plan 18.3). One
// trim beyond content: the main app logs failed submits through utils/debugLog's
// debugError, a file outside this phase's file list (standardly-griha-poc/CLAUDE.md's
// "YOUR FILES"), so this copy calls console.error directly instead of adding that module.
export default function CreateStandardModal({ isOpen, onClose, onSubmit, isSubmitting }) {
  const [standardData, setStandardData] = useState({
    name: '',
    version: '',
    description: '',
    is_active: true,
    requirements: [
      {
        section: '1.1',
        subsection: '',
        title: '',
        description: '',
        criteria: '',
        mandatory: true,
        weight: 1.0,
        parent_section: '',
      },
    ],
  });

  const [errors, setErrors] = useState({});

  const resetForm = useCallback(() => {
    setStandardData({
      name: '',
      version: '',
      description: '',
      is_active: true,
      requirements: [
        {
          section: '1.1',
          subsection: '',
          title: '',
          description: '',
          criteria: '',
          mandatory: true,
          weight: 1.0,
          parent_section: '',
        },
      ],
    });
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    // Standard validation
    if (!standardData.name.trim()) {
      newErrors.name = 'Standard name is required';
    }
    if (!standardData.version.trim()) {
      newErrors.version = 'Version is required';
    }

    // Requirements validation
    const requirementErrors = [];
    standardData.requirements.forEach((req, index) => {
      const reqErrors = {};
      if (!req.section.trim()) {
        reqErrors.section = 'Section is required';
      }
      if (!req.title.trim()) {
        reqErrors.title = 'Title is required';
      }
      if (!req.description.trim()) {
        reqErrors.description = 'Description is required';
      }
      if (!req.criteria.trim()) {
        reqErrors.criteria = 'Criteria is required';
      }
      if (req.weight < 0.1 || req.weight > 10.0) {
        reqErrors.weight = 'Weight must be between 0.1 and 10.0';
      }

      if (Object.keys(reqErrors).length > 0) {
        requirementErrors[index] = reqErrors;
      }
    });

    if (Object.keys(requirementErrors).length > 0) {
      newErrors.requirements = requirementErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(standardData);
      resetForm();
    } catch (error) {
      // Parent shows the toast; this is only for local debugging.
      console.error('Failed to create standard:', error?.response?.status || error?.message);
    }
  };

  const addRequirement = () => {
    const newSection = `${standardData.requirements.length + 1}.1`;
    setStandardData((prev) => ({
      ...prev,
      requirements: [
        ...prev.requirements,
        {
          section: newSection,
          subsection: '',
          title: '',
          description: '',
          criteria: '',
          mandatory: true,
          weight: 1.0,
          parent_section: '',
        },
      ],
    }));
  };

  const removeRequirement = (index) => {
    if (standardData.requirements.length > 1) {
      setStandardData((prev) => ({
        ...prev,
        requirements: prev.requirements.filter((_, i) => i !== index),
      }));
    }
  };

  const updateRequirement = (index, field, value) => {
    setStandardData((prev) => ({
      ...prev,
      requirements: prev.requirements.map((req, i) =>
        i === index ? { ...req, [field]: value } : req
      ),
    }));
  };

  const updateStandardField = (field, value) => {
    setStandardData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Create New Certification Standard
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(90vh-8rem)]">
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Standard Information */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Standard Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={standardData.name}
                    onChange={(e) => updateStandardField('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., GRIHA"
                    disabled={isSubmitting}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Version *
                  </label>
                  <input
                    type="text"
                    value={standardData.version}
                    onChange={(e) => updateStandardField('version', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.version ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 6.0"
                    disabled={isSubmitting}
                  />
                  {errors.version && <p className="text-red-500 text-xs mt-1">{errors.version}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={standardData.description}
                  onChange={(e) => updateStandardField('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of the standard"
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={standardData.is_active}
                  onChange={(e) => updateStandardField('is_active', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isSubmitting}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active (available for use)
                </label>
              </div>
            </div>

            {/* Requirements Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900">
                  Requirements ({standardData.requirements.length})
                </h4>
                <button
                  type="button"
                  onClick={addRequirement}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Requirement
                </button>
              </div>

              <div className="space-y-6">
                {standardData.requirements.map((requirement, index) => (
                  <div key={index} className="bg-[#F8F8FF] rounded-lg p-4 relative">
                    {/* Remove button */}
                    {standardData.requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
                        disabled={isSubmitting}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Section *
                        </label>
                        <input
                          type="text"
                          value={requirement.section}
                          onChange={(e) => updateRequirement(index, 'section', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.requirements?.[index]?.section ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="e.g., C01"
                          disabled={isSubmitting}
                        />
                        {errors.requirements?.[index]?.section && (
                          <p className="text-red-500 text-xs mt-1">{errors.requirements[index].section}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subsection
                        </label>
                        <input
                          type="text"
                          value={requirement.subsection}
                          onChange={(e) => updateRequirement(index, 'subsection', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Optional subsection"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={requirement.title}
                        onChange={(e) => updateRequirement(index, 'title', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.requirements?.[index]?.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Requirement title"
                        disabled={isSubmitting}
                      />
                      {errors.requirements?.[index]?.title && (
                        <p className="text-red-500 text-xs mt-1">{errors.requirements[index].title}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <textarea
                        value={requirement.description}
                        onChange={(e) => updateRequirement(index, 'description', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.requirements?.[index]?.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                        rows={2}
                        placeholder="Detailed description"
                        disabled={isSubmitting}
                      />
                      {errors.requirements?.[index]?.description && (
                        <p className="text-red-500 text-xs mt-1">{errors.requirements[index].description}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assessment Criteria *
                      </label>
                      <textarea
                        value={requirement.criteria}
                        onChange={(e) => updateRequirement(index, 'criteria', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.requirements?.[index]?.criteria ? 'border-red-500' : 'border-gray-300'
                        }`}
                        rows={2}
                        placeholder="How this requirement will be evaluated"
                        disabled={isSubmitting}
                      />
                      {errors.requirements?.[index]?.criteria && (
                        <p className="text-red-500 text-xs mt-1">{errors.requirements[index].criteria}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`mandatory_${index}`}
                          checked={requirement.mandatory}
                          onChange={(e) => updateRequirement(index, 'mandatory', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          disabled={isSubmitting}
                        />
                        <label htmlFor={`mandatory_${index}`} className="ml-2 block text-sm text-gray-700">
                          Mandatory
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Weight
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          max="10.0"
                          value={requirement.weight}
                          onChange={(e) => updateRequirement(index, 'weight', parseFloat(e.target.value) || 1.0)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.requirements?.[index]?.weight ? 'border-red-500' : 'border-gray-300'
                          }`}
                          disabled={isSubmitting}
                        />
                        {errors.requirements?.[index]?.weight && (
                          <p className="text-red-500 text-xs mt-1">{errors.requirements[index].weight}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Parent Section
                        </label>
                        <input
                          type="text"
                          value={requirement.parent_section}
                          onChange={(e) => updateRequirement(index, 'parent_section', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Optional parent"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info note */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Tips:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Use clear section numbers (1.1, 2.3, etc.)</li>
                      <li>Weight determines importance in scoring (higher = more impact)</li>
                      <li>Parent sections create hierarchical relationships</li>
                      <li>Mark critical requirements as mandatory</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Standard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
